import React, { useEffect } from 'react';

import styles from './DeleteDashboardModal.module.css';

export interface DeleteDashboardModalProps {
  open: boolean;
  dashboardName: string;
  deleting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export const DeleteDashboardModal: React.FC<DeleteDashboardModalProps> = ({
  open,
  dashboardName,
  deleting,
  errorMessage,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !deleting) {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, deleting, onCancel]);

  if (!open) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (deleting) return;
    if (event.target === event.currentTarget) onCancel();
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="delete-dashboard-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dashboard-modal-title"
      >
        <h2 id="delete-dashboard-modal-title" className={styles.title}>
          Delete dashboard
        </h2>
        <p className={styles.body}>
          Delete <span className={styles.dashboardName}>{dashboardName}</span>?
        </p>
        <p className={styles.note}>
          This removes the dashboard and any email schedule attached to it. This
          action cannot be undone.
        </p>
        {errorMessage ? (
          <p className={styles.formError} data-testid="delete-dashboard-error">
            {errorMessage}
          </p>
        ) : null}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={deleting}
            data-testid="delete-dashboard-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.dangerButton}
            onClick={onConfirm}
            disabled={deleting}
            data-testid="delete-dashboard-confirm"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};
